import { useEffect, useRef, useState } from 'react'
import { attachmentContentUrl } from '../../api/endpoints'
import type { Attachment } from '../../api/types'
import { Icon } from '../../components/Icon'
import { formatDuration, knownDuration } from '../../lib/media'
import { ambientAudio, playbackAudio } from '../../sound/audioSession'
import { ClipFailed } from './ClipFailed'
import { clipVersion, clipView } from './clipView'
import { Waveform } from './Waveform'

interface Props {
  attachment: Attachment
  /** Still in the outbox. */
  pending: boolean
  /** Sent by me, so I may fetch it before it's converted. */
  mine: boolean
}

/** An audio file or a voice message (spec §8), or why it can't play yet. */
export function AudioAttachment({ attachment, pending, mine }: Props) {
  const view = clipView(attachment, { pending, mine })
  if (view.state === 'failed') return <ClipFailed attachment={attachment} download={view.download} />
  const waiting = view.state === 'sending' ? 'Sending…' : view.state === 'processing' && !view.playable ? 'Processing audio…' : null
  return <AudioPlayer key={clipVersion(attachment)} attachment={attachment} waiting={waiting} processing={view.state === 'processing'} />
}

const SPEEDS = [1, 1.5, 2] as const

interface PlayerProps {
  attachment: Pick<Attachment, 'id' | 'fileName' | 'voice' | 'durationMs' | 'waveform'>
  /** Why it can't be played yet ("Processing audio…"), or null when it can. */
  waiting: string | null
  processing: boolean
  /** Where to play it from instead of the attachment's own file: a recording not sent yet. */
  src?: string
  /** What its controls call it, instead of "voice message" or the file name. */
  name?: string
  /** A voice message's speed button: not for listening back to a recording. */
  speeds?: boolean
}

/**
 * A compact player: play and pause, where it's up to (which moves it, by pointer or arrow keys) and its length. A voice
 * message shows its waveform as the progress and plays at 1×, 1.5× or 2×; an audio file shows its name. The file is fetched
 * only once someone plays it.
 */
export function AudioPlayer({ attachment, waiting, processing, src, name: calledAs, speeds = true }: PlayerProps) {
  const audio = useRef<HTMLAudioElement>(null)
  const playButton = useRef<HTMLButtonElement>(null)
  const [playing, setPlaying] = useState(false)
  const [position, setPosition] = useState(0)
  const [measured, setMeasured] = useState<number | null>(null)
  const [speed, setSpeed] = useState<(typeof SPEEDS)[number]>(1)
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const voice = attachment.voice
  const name = calledAs ?? (voice ? 'voice message' : attachment.fileName)
  const known = knownDuration(attachment.durationMs)
  // Seconds: what the file says once loaded, else what the server measured (or the app estimated).
  const duration = measured ?? (known !== null ? known / 1000 : null)
  const playable = waiting === null && !failed

  const toggle = () => {
    const element = audio.current
    if (!element) return
    if (!element.paused) {
      element.pause()
      return
    }
    element.playbackRate = speed
    // Out loud on an iPhone with its silent switch on, like any clip someone starts.
    playbackAudio()
    element.play().catch((error: unknown) => {
      ambientAudio()
      if (error instanceof DOMException && (error.name === 'AbortError' || error.name === 'NotAllowedError')) return
      setFailed(true)
    })
  }

  // Trying again: focus goes back to play, where the button that was pressed has gone.
  useEffect(() => {
    if (attempt > 0) playButton.current?.focus()
  }, [attempt])

  const seek = (seconds: number) => {
    setPosition(seconds)
    if (audio.current) audio.current.currentTime = seconds
  }

  const shown = playing || position > 0 ? position : (duration ?? 0)
  const speedLabel = `${speed}×`

  return (
    <div className={voice ? 'audio-card voice-card' : 'audio-card'}>
      <button
        ref={playButton}
        type="button"
        className="audio-play"
        aria-label={`${playing ? 'Pause' : 'Play'} ${name}${duration ? `, ${formatDuration(duration * 1000)}` : ''}`}
        disabled={!playable}
        onClick={toggle}
      >
        <Icon name={playing ? 'pause' : 'play'} />
      </button>
      <div className="audio-body">
        {!voice ? (
          <strong className="audio-name" title={attachment.fileName}>
            {attachment.fileName}
          </strong>
        ) : null}
        <div className="audio-track">
          {voice ? (
            <Waveform levels={attachment.waveform} progress={duration ? position / duration : 0} />
          ) : (
            <span className="audio-progress" aria-hidden="true">
              <span style={{ width: `${duration ? Math.min(100, (position / duration) * 100) : 0}%` }} />
            </span>
          )}
          <input
            type="range"
            className="audio-seek"
            min={0}
            max={duration ?? 0}
            step={duration !== null && duration < 10 ? 0.1 : 1}
            value={Math.min(position, duration ?? 0)}
            disabled={!playable || !duration}
            aria-label={`Position in ${name}`}
            aria-valuetext={`${formatDuration(position * 1000)} of ${duration ? formatDuration(duration * 1000) : 'unknown length'}`}
            onChange={(event) => seek(Number(event.target.value))}
          />
        </div>
        <span className="audio-meta">
          {/* Screen readers have the length in the button and where it's up to in the slider: this changes as it plays. */}
          {duration !== null || position > 0 ? (
            <span className="audio-time" aria-hidden="true">
              {formatDuration(shown * 1000)}
            </span>
          ) : null}
          {waiting ? (
            <span className="clip-waiting">{waiting}</span>
          ) : failed ? (
            <span className="clip-waiting is-error">
              Couldn't play it.{' '}
              <button
                type="button"
                className="text-button"
                onClick={() => {
                  setFailed(false)
                  setPlaying(false)
                  setAttempt((n) => n + 1)
                }}
              >
                Try again
              </button>
            </span>
          ) : processing ? (
            <span className="clip-waiting">Processing audio…</span>
          ) : null}
        </span>
      </div>
      {voice && speeds ? (
        <button
          type="button"
          className="voice-speed"
          aria-label={`Speed ${speedLabel}`}
          disabled={waiting !== null}
          onClick={() => {
            const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length]
            setSpeed(next)
            if (audio.current) audio.current.playbackRate = next
          }}
        >
          {speedLabel}
        </button>
      ) : null}
      {waiting === null ? (
        <audio
          key={attempt}
          ref={audio}
          src={src ?? attachmentContentUrl(attachment.id, 'original')}
          preload="none"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => {
            setPlaying(false)
            setPosition(0)
          }}
          onTimeUpdate={(event) => setPosition(event.currentTarget.currentTime)}
          onDurationChange={(event) => {
            const seconds = event.currentTarget.duration
            if (Number.isFinite(seconds) && seconds > 0) setMeasured(seconds)
          }}
          onError={() => {
            setPlaying(false)
            setFailed(true)
          }}
        />
      ) : null}
    </div>
  )
}
